// Central EditorCapabilities model for adaptive control display
// Based on chart type, data roles, and actual Plotly figure structure

import { ChartCardV1 } from '../../../../../../packages/db-schema/shared/chartcards/types';

export type AxisType = 'numeric' | 'categorical' | 'ordered' | 'datetime' | 'none';
export type CoordinateSystem = 'cartesian' | 'polar' | 'radial' | 'network' | 'matrix' | 'timeline';

export interface EditorCapabilities {
  chartId: string;
  family: string;
  coordSystem: CoordinateSystem;
  axes: {
    hasX: boolean;
    hasY: boolean;
    xType: AxisType;
    yType: AxisType;
    secondaryYSupported: boolean;
  };
  encodings: {
    hasColor: boolean;
    hasSize: boolean;
    hasSymbol: boolean;
    hasText: boolean;
    hasWeight: boolean;
    hasFacet: boolean;
  };
  traces: {
    hasBars: boolean;
    hasHistogram: boolean;
    hasScatter: boolean;
    hasLine: boolean;
    hasViolin: boolean;
    hasBox: boolean;
    hasPieLike: boolean;
    hasCartesian: boolean;
  };
  supports: {
    orientation: boolean;          // bar/box/violin
    barGrouping: boolean;          // bar family + cartesian
    barNormalization: boolean;     // bar / histogram
    histogramBinning: boolean;     // histogram, hexbin
    categoryOrderX: boolean;       // x categorical
    categoryOrderY: boolean;       // y categorical
    perPointStyle: boolean;        // scatter-like, volcano, etc.
    journalTemplates: boolean;     // most cartesian charts
    legend: boolean;               // any multi-series charts
    axesTab: boolean;              // only cartesian / timeline
  };
  special: {
    volcano: boolean;
    forest: boolean;
    hexbin: boolean;
    ridgeline: boolean;
    gantt: boolean;
    candlestick: boolean;
    radial: boolean;               // radar, polar_area
    flow: boolean;                 // sankey / chord
    treemap: boolean;
  };
}

export interface DeriveCapabilitiesArgs {
  chartCard?: ChartCardV1 | null;
  planSpec?: any;
  plotlyJson?: any;
  dataColumns?: string[];
}

/**
 * Derives EditorCapabilities from chart card, plan spec, and actual Plotly figure.
 * Uses both declarative metadata (chartCard) and actual figure structure (plotlyJson).
 */
export function deriveEditorCapabilities({
  chartCard,
  planSpec,
  plotlyJson,
  dataColumns = [],
}: DeriveCapabilitiesArgs): EditorCapabilities {
  const traces = Array.isArray(plotlyJson?.data) ? plotlyJson.data : [];
  const layout = plotlyJson?.layout || {};
  
  // Extract chart metadata
  const chartId = chartCard?.chart_id || 
                  plotlyJson?.meta?.chart_type || 
                  plotlyJson?.meta?.lumi?.chart_type || 
                  '';
  const family = chartCard?.family || 
                 planSpec?.chart_family || 
                 inferFamilyFromTraces(traces) || 
                 'scatter';
  
  // Detect coordinate system
  const coordSystem = detectCoordinateSystem(layout, traces);
  
  // Detect axes
  const hasX = !!(layout.xaxis || traces.some((t: any) => t.x !== undefined));
  const hasY = !!(layout.yaxis || traces.some((t: any) => t.y !== undefined));
  const xType = detectAxisType(layout.xaxis);
  const yType = detectAxisType(layout.yaxis);
  const secondaryYSupported = !!(layout.yaxis2 || coordSystem === 'cartesian');
  
  // Detect encodings from roles or traces
  const encodings = detectEncodings(chartCard, planSpec, traces);
  
  // Detect trace types
  const traceTypes = {
    hasBars: traces.some((t: any) => t.type === 'bar'),
    hasHistogram: traces.some((t: any) => t.type === 'histogram' || t.type === 'histogram2d'),
    hasScatter: traces.some((t: any) => t.type === 'scatter' || t.type === 'scattergl'),
    hasLine: traces.some((t: any) => (t.type === 'scatter' || !t.type) && t.mode?.includes('lines')),
    hasViolin: traces.some((t: any) => t.type === 'violin'),
    hasBox: traces.some((t: any) => t.type === 'box'),
    hasPieLike: traces.some((t: any) => ['pie', 'funnelarea', 'donut'].includes(t.type)),
    hasCartesian: coordSystem === 'cartesian',
  };
  
  // Detect special chart types
  const special = {
    volcano: chartId === 'volcano' || layout.meta?.chart_type === 'volcano',
    forest: chartId === 'forest' || layout.meta?.chart_type === 'forest',
    hexbin: chartId === 'hexbin' || traces.some((t: any) => t.type === 'histogram2d'),
    ridgeline: chartId === 'ridgeline' || layout.meta?.chart_type === 'ridgeline',
    gantt: chartId === 'gantt' || layout.meta?.chart_type === 'gantt',
    candlestick: traces.some((t: any) => t.type === 'candlestick'),
    radial: traces.some((t: any) => ['scatterpolar', 'barpolar'].includes(t.type)) || coordSystem === 'polar',
    flow: traces.some((t: any) => ['sankey', 'chord'].includes(t.type)),
    treemap: traces.some((t: any) => t.type === 'treemap'),
  };
  
  // Derive support flags
  const supports = {
    orientation: traceTypes.hasBars || traceTypes.hasBox || traceTypes.hasViolin,
    barGrouping: traceTypes.hasBars && traceTypes.hasCartesian,
    barNormalization: traceTypes.hasBars || traceTypes.hasHistogram,
    histogramBinning: traceTypes.hasHistogram || special.hexbin,
    categoryOrderX: xType === 'categorical' && hasX,
    categoryOrderY: yType === 'categorical' && hasY,
    perPointStyle: traceTypes.hasScatter || (traceTypes.hasBars && traces.some((t: any) => Array.isArray(t.marker?.color))),
    journalTemplates: (traceTypes.hasCartesian || coordSystem === 'matrix') && !traces.every((t: any) => t.type === 'waterfall'),
    legend: traces.length > 1 || traces.some((t: any) => t.name),
    axesTab: coordSystem === 'cartesian' || coordSystem === 'timeline',
  };
  
  return {
    chartId,
    family,
    coordSystem,
    axes: {
      hasX,
      hasY,
      xType,
      yType,
      secondaryYSupported,
    },
    encodings,
    traces: traceTypes,
    supports,
    special,
  };
}

// Helper functions

function inferFamilyFromTraces(traces: any[]): string {
  if (traces.length === 0) return 'scatter';
  
  const types = traces.map((t: any) => t.type || 'scatter');
  if (types.some((t: string) => t === 'bar')) return 'bar';
  if (types.some((t: string) => t === 'histogram')) return 'distribution';
  if (types.some((t: string) => t === 'pie' || t === 'funnelarea')) return 'radial';
  if (types.some((t: string) => t === 'heatmap' || t === 'contour')) return 'matrix';
  if (types.some((t: string) => t === 'sankey')) return 'network';
  if (types.some((t: string) => t === 'scatter' || !t)) {
    const hasLines = traces.some((t: any) => t.mode?.includes('lines'));
    return hasLines ? 'trend' : 'scatter';
  }
  return 'scatter';
}

function detectCoordinateSystem(layout: any, traces: any[]): CoordinateSystem {
  // Check for explicit coordinate system indicators
  if (layout.polar) return 'polar';
  if (layout.ternary) return 'radial';
  if (traces.some((t: any) => t.type === 'sankey' || t.type === 'chord')) return 'network';
  if (traces.some((t: any) => t.type === 'heatmap' || t.type === 'contour')) return 'matrix';
  if (traces.some((t: any) => t.type === 'pie' || t.type === 'funnelarea')) return 'radial';
  if (layout.xaxis && layout.yaxis) return 'cartesian';
  if (layout.xaxis || layout.yaxis) return 'cartesian'; // Partial axes still cartesian
  
  // Default to cartesian if we have any traces with x/y
  if (traces.some((t: any) => t.x !== undefined || t.y !== undefined)) {
    return 'cartesian';
  }
  
  return 'cartesian'; // Safe default
}

function detectAxisType(axis: any): AxisType {
  if (!axis) return 'none';
  
  const type = String(axis.type || '').toLowerCase();
  if (type === 'log' || type === 'linear') return 'numeric';
  if (type === 'category') return 'categorical';
  if (type === 'date' || type === 'time') return 'datetime';
  
  // Infer from data if type not explicitly set
  if (axis.categoryorder) return 'categorical';
  
  return 'numeric'; // Default assumption
}

function detectEncodings(
  chartCard: ChartCardV1 | null | undefined,
  planSpec: any,
  traces: any[]
): EditorCapabilities['encodings'] {
  // Try to detect from chartCard roles
  const roles = chartCard?.roles as any;
  const requiredRoles = roles?.required || {};
  const optionalRoles = roles?.optional || {};
  
  // Check for color encoding
  const hasColor = !!(requiredRoles.color || optionalRoles.color || 
                      traces.some((t: any) => t.marker?.color || t.fillcolor || t.line?.color));
  
  // Check for size encoding
  const hasSize = !!(requiredRoles.size || optionalRoles.size || 
                     traces.some((t: any) => t.marker?.size || Array.isArray(t.marker?.size)));
  
  // Check for symbol encoding
  const hasSymbol = !!(traces.some((t: any) => t.marker?.symbol || Array.isArray(t.marker?.symbol)));
  
  // Check for text encoding
  const hasText = !!(requiredRoles.text || optionalRoles.text || 
                     traces.some((t: any) => t.text || t.textposition));
  
  // Check for weight encoding (less common)
  const hasWeight = !!(requiredRoles.weight || optionalRoles.weight);
  
  // Check for facet encoding
  const hasFacet = !!(planSpec?.encodings?.facet || 
                      traces.some((t: any) => t.xaxis || t.yaxis));
  
  return {
    hasColor,
    hasSize,
    hasSymbol,
    hasText,
    hasWeight,
    hasFacet,
  };
}

